import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../layouts/Layout';
import calendarIcon from '../../assets/img3d/calendar.png';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Calendar, ChevronLeft, ChevronRight, Clock, MapPin, User, CheckCircle, Phone, Mail, Home, Eye } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfessionalVisitsPage = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [visits, setVisits] = useState({});
  const [upcomingVisits, setUpcomingVisits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCalendarVisits();
    fetchUpcomingVisits();
  }, [currentDate]);

  const fetchCalendarVisits = async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const response = await axios.get(`${API}/professional/visits/calendar?year=${year}&month=${month}`);
      setVisits(response.data || {});
    } catch (error) {
      console.error('Error fetching calendar visits:', error);
      setVisits({});
    }
  };

  const fetchUpcomingVisits = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/professional/visits?upcomingOnly=true`);
      const visitsData = Array.isArray(response.data) ? response.data : [];
      setUpcomingVisits(visitsData);
    } catch (error) {
      console.error('Error fetching upcoming visits:', error);
      setUpcomingVisits([]);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    
    return days;
  };

  const getVisitsForDay = (day) => {
    if (!day) return [];
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    const dateKey = `${year}-${month}-${dayStr}`;
    return visits[dateKey] || [];
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const isToday = (day) => {
    const today = new Date();
    return (
      day &&
      currentDate.getFullYear() === today.getFullYear() &&
      currentDate.getMonth() === today.getMonth() &&
      day === today.getDate()
    );
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Date invalide';
      return date.toLocaleString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Date invalide';
    }
  };

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

  const days = getDaysInMonth(currentDate);

  return (
    <Layout
      headerConfig={{
        icon: calendarIcon,
        iconColor: 'bg-white',
        title: 'Calendrier des Visites',
        subtitle: 'Planification et suivi de vos visites',
        primaryAction: 'Tableau de Bord',
        secondaryAction: undefined,
        tertiaryAction: undefined,
        onPrimaryAction: () => navigate('/professional'),
        onSecondaryAction: undefined,
        onTertiaryAction: undefined
      }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar - Left Column */}
        <div className="lg:col-span-2">
          <Card>
            <CardContent className="p-6">
              {/* Calendar Header */}
              <div className="flex items-center justify-between mb-6">
                <Button variant="outline" onClick={previousMonth}>
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <h2 className="text-2xl font-bold">
                  {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                </h2>
                <Button variant="outline" onClick={nextMonth}>
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>

              {/* Day Names */}
              <div className="grid grid-cols-7 gap-2 mb-2">
                {dayNames.map((day) => (
                  <div key={day} className="text-center font-semibold text-gray-600 py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-2">
                {days.map((day, index) => {
                  const dayVisits = getVisitsForDay(day);
                  const hasVisits = dayVisits.length > 0;

                  return (
                    <div
                      key={index}
                      className={`
                        min-h-[120px] p-2 rounded-lg border-2 transition-colors
                        ${day ? 'bg-white hover:bg-gray-50' : 'bg-gray-100'}
                        ${isToday(day) ? 'border-[#FF9F68] bg-orange-50' : 'border-gray-200'}
                        ${hasVisits ? 'cursor-pointer' : ''}
                      `}
                    >
                      {day && (
                        <>
                          <div className="flex items-center justify-between mb-2">
                            <span className={`
                              text-sm font-semibold
                              ${isToday(day) ? 'text-[#FF9F68]' : 'text-gray-900'}
                            `}>
                              {day}
                            </span>
                            {hasVisits && (
                              <Badge className="bg-[#FF9F68] text-white text-xs">
                                {dayVisits.length}
                              </Badge>
                            )}
                          </div>

                          {/* Visits for this day */}
                          <div className="space-y-1">
                            {dayVisits.slice(0, 2).map((visit) => {
                              const seekerName = visit.seekerName || visit.seeker_name;
                              const propertyTitle = visit.propertyTitle || visit.property_title;
                              const visitCompleted = visit.visitCompleted || visit.visit_completed;
                              
                              return (
                                <div
                                  key={visit.id}
                                  onClick={() => navigate(`/professional/leads/${visit.id}`)}
                                  className="text-xs p-2 bg-blue-50 rounded border border-blue-200 hover:bg-blue-100 transition-colors cursor-pointer"
                                >
                                  <div className="flex items-center space-x-1 mb-1">
                                    <Clock className="h-3 w-3 text-blue-600" />
                                    <span className="font-semibold text-blue-900">{visit.time}</span>
                                    {visitCompleted && (
                                      <CheckCircle className="h-3 w-3 text-green-600 ml-auto" />
                                    )}
                                  </div>
                                  <div className="text-gray-700 truncate font-medium">{seekerName}</div>
                                  {propertyTitle && (
                                    <div className="text-gray-600 truncate text-[10px] mt-0.5">{propertyTitle}</div>
                                  )}
                                </div>
                              );
                            })}

                            {dayVisits.length > 2 && (
                              <div className="text-xs text-center text-gray-600 py-1">
                                +{dayVisits.length - 2} autre(s)
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="mt-6 pt-6 border-t flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-orange-50 border-2 border-[#FF9F68] rounded"></div>
                  <span className="text-sm text-gray-600">Aujourd'hui</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-blue-50 border border-blue-200 rounded"></div>
                  <span className="text-sm text-gray-600">Visite planifiée</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-gray-600">Visite effectuée</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Upcoming Visits - Right Column */}
        <div>
          <Card>
            <CardContent className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Prochaines Visites</h3>
              
              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF9F68]"></div>
                </div>
              ) : upcomingVisits.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">Aucune visite planifiée</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {upcomingVisits.slice(0, 5).map((visit) => {
                    const visitDate = visit.visitDate || visit.visit_date;
                    const seekerName = visit.seekerName || visit.seeker_name;
                    const seekerEmail = visit.seekerEmail || visit.seeker_email;
                    const seekerPhone = visit.seekerPhone || visit.seeker_phone;
                    const property = visit.property || {};
                    const propertyTitle = property.title || visit.property_title;
                    const propertyLocation = property.location || visit.property_address;
                    const visitNotes = visit.visitNotes || visit.visit_notes;
                    const visitCompleted = visit.visitCompleted || visit.visit_completed;
                    
                    return (
                      <div
                        key={visit.id}
                        className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Clock className="h-4 w-4 text-[#FF9F68]" />
                              <span className="font-semibold text-gray-900 text-sm">
                                {formatDateTime(visitDate)}
                              </span>
                            </div>
                            
                            <div className="space-y-1 mb-3">
                              <div className="flex items-center gap-2 text-sm">
                                <User className="h-3 w-3 text-gray-500" />
                                <span className="font-medium text-gray-900">{seekerName}</span>
                              </div>
                              
                              {propertyTitle && (
                                <div className="flex items-center gap-2 text-sm">
                                  <Home className="h-3 w-3 text-gray-500" />
                                  <span className="text-gray-700">{propertyTitle}</span>
                                </div>
                              )}
                              
                              {propertyLocation && (
                                <div className="flex items-center gap-2 text-sm">
                                  <MapPin className="h-3 w-3 text-gray-500" />
                                  <span className="text-gray-600 text-xs">{propertyLocation}</span>
                                </div>
                              )}
                            </div>

                            <div className="flex items-center gap-2 mb-2">
                              {seekerEmail && (
                                <a
                                  href={`mailto:${seekerEmail}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="p-1.5 bg-white rounded border border-gray-300 hover:bg-orange-50 hover:border-[#FF9F68] transition-colors"
                                  title="Envoyer un email"
                                >
                                  <Mail className="h-3 w-3 text-gray-600" />
                                </a>
                              )}
                              {seekerPhone && (
                                <a
                                  href={`tel:${seekerPhone}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="p-1.5 bg-white rounded border border-gray-300 hover:bg-orange-50 hover:border-[#FF9F68] transition-colors"
                                  title="Appeler"
                                >
                                  <Phone className="h-3 w-3 text-gray-600" />
                                </a>
                              )}
                            </div>

                            {visitNotes && (
                              <div className="text-xs text-gray-600 bg-white p-2 rounded border border-gray-200 mb-2">
                                <strong>Notes:</strong> {visitNotes}
                              </div>
                            )}

                            {visitCompleted && (
                              <Badge className="bg-green-100 text-green-800 text-xs">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Visite effectuée
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/professional/leads/${visit.id}`)}
                          className="w-full mt-2"
                        >
                          <Eye className="h-3 w-3 mr-2" />
                          Voir les détails
                        </Button>
                      </div>
                    );
                  })}
                  
                  {upcomingVisits.length > 5 && (
                    <Button
                      variant="outline"
                      onClick={() => navigate('/professional/leads')}
                      className="w-full"
                    >
                      Voir toutes les visites
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default ProfessionalVisitsPage;

